import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowClockwise, DownloadSimple } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import useBranches from "@/hooks/useBranches";
import PartsGroupTable, { inr } from "@/components/serviceParts/PartsGroupTable";
import { buildPartsCsv, downloadCsv } from "@/lib/servicePartsCsv";

const todayISO = () => new Date().toISOString().slice(0, 10);
const monthsAgoISO = (n) => { const d = new Date(); d.setMonth(d.getMonth() - n); return d.toISOString().slice(0, 10); };
const field = "px-3 py-2 bg-canvas border border-canvas-line text-sm";
const lbl = "text-[10px] uppercase tracking-widest text-muted-foreground";
const ADV_TYPE = { direct_parts: "Direct parts", accident_engine: "Accident / engine" };

const JC_COLS = [
  { label: "JC #", render: (r) => r.jc_no, cls: "font-mono text-xs font-semibold" },
  { label: "JC Date", render: (r) => r.jc_date || "—", cls: "font-mono text-xs" },
  { label: "Status", render: (r) => r.jc_status, cls: "font-mono text-[10px] uppercase" },
  { label: "Closed", render: (r) => r.closed_on || "—", cls: "font-mono text-xs" },
  { label: "Duration", render: (r) => (r.jc_duration_days ?? "—") + (r.jc_open ? "d (open)" : "d"), cls: "font-mono text-xs whitespace-nowrap" },
  { label: "Branch", render: (r) => r.branch_name, cls: "text-xs" },
  { label: "Customer", render: (r) => r.customer_name, cls: "text-xs" },
  { label: "Vehicle", render: (r) => r.vehicle_reg_no || "—", cls: "font-mono text-xs" },
  { label: "Advances", render: (r) => r.advance_nos, cls: "font-mono text-[10px]" },
];
const ADV_COLS = [
  { label: "Advance #", render: (r) => r.advance_no, cls: "font-mono text-xs font-semibold" },
  { label: "Date", render: (r) => r.date, cls: "font-mono text-xs" },
  { label: "Type", render: (r) => ADV_TYPE[r.advance_type] || r.advance_type, cls: "text-xs" },
  { label: "Branch", render: (r) => r.branch_name, cls: "text-xs" },
  { label: "Customer", render: (r) => `${r.customer_name} ${r.customer_phone}`, cls: "text-xs" },
  { label: "Vehicle", render: (r) => r.vehicle_reg_no || "—", cls: "font-mono text-xs" },
  { label: "JC #", render: (r) => r.jc_no || "—", cls: "font-mono text-xs" },
  { label: "Advance", render: (r) => `${inr(r.advance_amount)} · bal ${inr(r.advance_remaining)}`, cls: "font-mono text-xs whitespace-nowrap" },
];

export default function ServicePartsOrdersReportPage() {
  const [f, setF] = useState({ from: monthsAgoISO(3), to: todayISO(), branchId: "", orderType: "" });
  const [tab, setTab] = useState("jc");
  const [pendingOnly, setPendingOnly] = useState(true);
  const [data, setData] = useState({ job_cards: [], advances: [], summary: {} });
  const [loading, setLoading] = useState(false);
  const { branches } = useBranches({ activeOnly: true, includeAll: true });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date_from: f.from || undefined, date_to: f.to || undefined, branch_id: f.branchId || undefined, order_type: f.orderType || undefined };
      const { data: d } = await api.get("/reports/service-parts-orders", { params });
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [f]);
  useEffect(() => { load(); }, [load]);

  const view = useMemo(() => {
    const keep = (r) => !pendingOnly || r.parts_on_order + r.parts_received > 0;
    return { job_cards: data.job_cards.filter(keep), advances: data.advances.filter(keep) };
  }, [data, pendingOnly]);

  const exportCsv = () => {
    if (!(tab === "jc" ? view.job_cards : view.advances).length) return toast.error("No rows to export");
    downloadCsv(buildPartsCsv(tab, view), `service-parts-${tab === "jc" ? "jobcards" : "advances"}-${f.from}-${f.to}.csv`);
  };
  const s = data.summary || {};

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Reports" title="Service Parts Orders"
        subtitle={`${s.parts_total ?? 0} parts · ${s.parts_on_order ?? 0} on order · ${s.parts_received ?? 0} arrived (not fitted) · ${s.parts_fitted ?? 0} fitted · ${s.parts_overdue ?? 0} overdue · avg ${s.avg_days_to_arrive ?? "—"} days to arrive`} />

      <div className="px-8 pb-8 space-y-6">
      <div className="flex items-end gap-3 flex-wrap">
        <label className="flex flex-col gap-1"><span className={lbl}>From (advance date)</span>
          <input type="date" value={f.from} onChange={set("from")} data-testid="spo-from" className={field} /></label>
        <label className="flex flex-col gap-1"><span className={lbl}>To</span>
          <input type="date" value={f.to} onChange={set("to")} data-testid="spo-to" className={field} /></label>
        <label className="flex flex-col gap-1 min-w-[180px]"><span className={lbl}>Branch</span>
          <select value={f.branchId} onChange={set("branchId")} data-testid="spo-branch" className={field}>
            <option value="">All branches</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select></label>
        <label className="flex flex-col gap-1"><span className={lbl}>Order type</span>
          <select value={f.orderType} onChange={set("orderType")} data-testid="spo-order-type" className={field}>
            <option value="">All</option>{["APS", "VOR", "DEALER"].map((t) => <option key={t} value={t}>{t}</option>)}
          </select></label>
        <label className="flex items-center gap-2 text-xs pb-2.5">
          <input type="checkbox" checked={pendingOnly} onChange={(e) => setPendingOnly(e.target.checked)} data-testid="spo-pending-only" />
          Only with parts still on order / not fitted
        </label>
        <button onClick={load} disabled={loading} data-testid="spo-apply"
          className="px-4 py-2 bg-ink text-white text-xs font-mono uppercase tracking-wider hover:bg-brand disabled:opacity-50 flex items-center gap-1.5">
          <ArrowClockwise size={12} /> {loading ? "Loading…" : "Apply"}
        </button>
        <button onClick={exportCsv} data-testid="spo-export"
          className="px-4 py-2 border border-canvas-line text-xs font-mono uppercase tracking-wider hover:border-ink flex items-center gap-1.5">
          <DownloadSimple size={12} /> Export CSV
        </button>
      </div>

      <div className="flex gap-0 border-b border-canvas-line">
        {[["jc", `By Job Card (${view.job_cards.length})`], ["adv", `Customer Advances (${view.advances.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`spo-tab-${k}`}
            className={`px-4 py-2 text-xs font-mono uppercase tracking-wider border-b-2 -mb-px transition-colors ${tab === k ? "border-ink text-ink" : "border-transparent text-ink-muted hover:text-ink"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "jc" ? (
        <PartsGroupTable rows={view.job_cards} columns={JC_COLS} rowKey={(r) => r.jc_no} showAdvance testPrefix="spo-jc"
          emptyText="No job cards with ordered parts. Parts are ordered through a customer advance (Direct parts / Accident-engine) carrying the JC number." />
      ) : (
        <PartsGroupTable rows={view.advances} columns={ADV_COLS} rowKey={(r) => r.advance_no || r.advance_id} testPrefix="spo-adv"
          emptyText="No customer advances with parts in this period." />
      )}
      <p className="text-[11px] text-ink-muted">Click a row to see each part. Red = expected date passed and part not yet arrived. Durations for open items count up to today.</p>
      </div>
    </div>
  );
}
